import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { visuallyHidden } from "@mui/utils";
import { useCallback, useState, type ReactNode } from "react";

/**
 * One thing a row can have done to it. `label` is the verb, in the imperative — it is the tooltip
 * when the action is an icon and the menu text when it is not, so it has to read correctly in both.
 */
export type RowAction = {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /**
   * Marks an action that destroys something — delete, cancel, remove — and colours it accordingly.
   *
   * It is the only action that gets a colour, and that is the point: colour as a signal works while
   * it is scarce. Every icon in the app is a `SvgIcon`, whose default `color="inherit"` takes the
   * colour of the button around it, so the actions carry no colour of their own and none should be
   * passed. See [table row actions](../../../../../docs/software-design/table-row-actions.md).
   */
  destructive?: boolean;
  disabled?: boolean;
  /** Replaces `label` while `disabled`, to say why rather than leave the user guessing. */
  disabledReason?: string;
  /**
   * Accessible name, where the visible label is not enough on its own. A column of buttons all
   * called "Delete" tells a screen reader nothing about which row it is on, so name the row here —
   * "Delete Acme Corp" — and leave `label` as the verb the sighted user reads. Defaults to `label`.
   */
  ariaLabel?: string;
};

const RowActionsContainer = styled("div")({
  display: "flex",
  flexWrap: "nowrap",
  // The cell's own `align` cannot reach these: a block-level flex container fills the cell and packs
  // its buttons at the start whatever the cell says about text alignment.
  justifyContent: "center",
  whiteSpace: "nowrap",
});

const HiddenHeaderLabel = styled("span")({ ...visuallyHidden });

/**
 * The actions column's header: named for screen readers, invisible to everyone else.
 *
 * It is the only header in a table that does nothing — it cannot be sorted, filtered, or reordered,
 * and it opens no column menu — so it is inert text where every other header is a control. The
 * row-select column has never had a visible one for the same reason. Hiding it is mostly about
 * saying the same thing on every page: it was shown on fifteen tables and hidden on two.
 *
 * It is not where the column's width goes, despite appearances. Measured on the team templates table
 * at 820px, hiding the label moved the column from 73px to 71px; the 24px of cell padding around a
 * 40px kebab is the rest. Narrowing that padding is the lever that actually buys width, and it is a
 * separate decision from this one.
 *
 * `display: none` would also hide the label, which is what two tables were doing, but that takes the
 * column's name away from a screen reader navigating cell by cell. Hiding it visually keeps it.
 */
export function RowActionsColumnHeader() {
  return <HiddenHeaderLabel>Actions</HiddenHeaderLabel>;
}

function describeAction(action: RowAction): string {
  return action.disabled && action.disabledReason ? action.disabledReason : action.label;
}

/**
 * The actions column for a table row: icons while there is room for them, one menu once there is not.
 *
 * Every table was rendering its own icons and none of them gave the width back when the table got
 * narrow, so three or four pinned icons ate the name column at high display scaling — the same
 * squeeze as #4964, except an actions column cannot be dropped the way a data column can. The
 * collapse is the part worth sharing: it was written once, on the communications page, and nowhere
 * else.
 *
 * The breakpoint is the viewport by default, which is the right question for a table that fills a
 * page. It is the wrong question inside a proposal pane — the navigation drawer and the comments
 * panel both take width the window cannot see — so those tables measure their own container and pass
 * `collapsed` themselves. See `useWorkPaneNarrowerThan`.
 */
export default function RowActions(
  props: Readonly<{
    actions: readonly RowAction[];
    /** Override the viewport test where the window is not the width that matters. */
    collapsed?: boolean;
    /** Names the menu button for screen readers, e.g. "Actions for Acme Corp". */
    ariaLabel?: string;
  }>,
) {
  const { actions, ariaLabel, collapsed } = props;
  // Read through `useTheme` rather than the callback form of `useMediaQuery`: the callback is handed
  // null where no provider is mounted, and a shared component should not require one.
  const theme = useTheme();
  const viewportIsNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeMenu = useCallback(() => setAnchorEl(null), []);

  const isCollapsed = collapsed ?? viewportIsNarrow;

  if (actions.length === 0) return null;

  if (!isCollapsed) {
    return (
      <RowActionsContainer>
        {actions.map((action) => (
          <Tooltip key={action.key} title={describeAction(action)}>
            {/* A disabled button emits no pointer events, so the tooltip needs a live element under it. */}
            <span>
              <IconButton
                aria-label={action.ariaLabel ?? action.label}
                // Colouring the button rather than the glyph tints the hover ripple with it, and
                // lets MRT's disabled styling win on its own — a `disabled` action greys out here
                // without the call site working the colour out for itself.
                color={action.destructive ? "error" : undefined}
                disabled={action.disabled}
                onClick={(event) => {
                  // Rows are usually clickable themselves — acting on one must not also open it.
                  event.stopPropagation();
                  action.onClick();
                }}
              >
                {action.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </RowActionsContainer>
    );
  }

  return (
    <RowActionsContainer>
      <IconButton
        aria-label={ariaLabel ?? "Row actions"}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        onClick={(event) => event.stopPropagation()}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.key}
            aria-label={action.ariaLabel}
            disabled={action.disabled}
            onClick={(event) => {
              event.stopPropagation();
              closeMenu();
              action.onClick();
            }}
          >
            <ListItemIcon sx={action.destructive ? { color: "error.main" } : undefined}>{action.icon}</ListItemIcon>
            <ListItemText>{describeAction(action)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </RowActionsContainer>
  );
}
